import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  inject,
  signal,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl, SafeUrl } from '@angular/platform-browser';
import { HttpResponse } from '@angular/common/http';
import { Observable, Subscription, map } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';

import { CourseMaterial } from '../../../../core/interfaces/course-material';
import { ExternalNavigationService } from '../../../../core/services/utils/external-navigation.service';

/** Rendu retenu pour le contenu ouvert. */
type PreviewKind = 'pdf' | 'image' | 'video' | 'audio' | 'external' | 'unsupported';

const PDF_TYPE = 'application/pdf';
const SVG_TYPE = 'image/svg+xml';

/**
 * Le type sous lequel un contenu s'affiche suit la catégorie calculée par le
 * serveur, jamais l'en-tête reçu.
 *
 * <p>Une URL `blob:` a l'origine de l'application : un « PDF » arrivé en
 * `text/html` devenait, dans l'iframe, une page qui s'exécutait avec un accès
 * complet à l'écran. Un PDF est donc toujours `application/pdf` — la
 * visionneuse du navigateur, qui n'exécute rien dans notre origine. Une image,
 * une vidéo, un son gardent leur type s'il est de leur famille ; sinon, des
 * octets bruts. Le SVG n'en est pas : ouvert seul, il exécute ses scripts, et
 * le dépôt le refuse déjà.</p>
 */
function retype(blob: Blob, kind: PreviewKind): Blob {
  if (kind === 'pdf') {
    return blob.type === PDF_TYPE ? blob : blob.slice(0, blob.size, PDF_TYPE);
  }

  const ofItsKind = blob.type.startsWith(`${kind}/`) && blob.type !== SVG_TYPE;
  return ofItsKind ? blob : blob.slice(0, blob.size, 'application/octet-stream');
}

/** Catégories que le lecteur lit en flux, par plages : il démarre sur les premiers octets. */
const STREAMED = new Set(['Video', 'Audio']);

/**
 * L'URL signée d'un support à lire en flux — vidéo et son seulement.
 *
 * <p>Par le téléchargement, une vidéo de la bibliothèque (près de 400 Mo en
 * moyenne, jusqu'à 1,7 Go) devait arriver en entier dans la mémoire du
 * navigateur avant la première image. Lue par son URL signée, elle démarre
 * dès les premiers octets et se parcourt comme sur un site de vidéo : le
 * lecteur ne demande que les plages qu'il joue. Le stockage les sert
 * (`Range`), et la mention « pièce jointe » de l'URL n'agit pas sur un
 * lecteur.</p>
 *
 * <p>Un PDF garde le téléchargement : ouverte dans un cadre, cette même URL
 * déclencherait l'enregistrement du fichier au lieu de l'afficher. Un support
 * resté sur le disque n'a pas d'URL signée : il passe aussi par le
 * téléchargement, comme avant.</p>
 */
function streamable(material: CourseMaterial): string | null {
  return STREAMED.has(material.category) ? material.previewUrl ?? null : null;
}

/**
 * Ce que l'aperçu sait montrer : un titre, la catégorie qui choisit le rendu
 * (`Pdf`, `Image`, `Video`, `Audio`), quelques lignes de contexte, et la
 * source — une **URL** que le navigateur lit en flux (lien signé, servi en
 * ligne : une vidéo d'une heure se parcourt sans attendre son téléchargement),
 * ou un lien externe qui s'ouvre chez son hébergeur. Sans URL, c'est le
 * chargeur `load` de l'hôte qui fournit le flux.
 */
export interface PreviewItem {
  title: string;
  category: string;
  meta?: string[];
  url?: string | null;
  externalUrl?: string | null;
}

/**
 * Aperçu d'un fichier — support de bibliothèque, enregistrement de séance.
 *
 * <p>Deux sources. Une URL lisible par le navigateur (lien signé) est donnée
 * telle quelle aux lecteurs : ils la lisent en flux, par plages, et abandonnent
 * à la fermeture. Un fichier qui exige le jeton (bibliothèque) passe par un
 * chargeur fourni par l'hôte : le flux est lu en mémoire et affiché depuis
 * une URL d'objet, révoquée à la fermeture.</p>
 *
 * <p><b>Lecture, pas téléchargement.</b> Ce qu'on regarde ici ne s'emporte
 * pas : pas de bouton d'enregistrement sur le lecteur, pas de barre sur la
 * visionneuse PDF, pas de menu contextuel. Ce n'est pas un coffre-fort — ce
 * qui s'affiche se capture — mais aucun geste de l'écran n'invite à copier.</p>
 *
 * <p>Le chargement attend que toutes les entrées soient posées (`ngOnChanges`) :
 * Angular les applique dans l'ordre du gabarit, et `[item]` lié avant `[load]`
 * chargerait sans chargeur.</p>
 */
@Component({
  selector: 'app-material-preview',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './material-preview.component.html',
  styleUrl: './material-preview.component.css',
})
export class MaterialPreviewComponent implements OnChanges {
  private readonly sanitizer = inject(DomSanitizer);
  private readonly navigation = inject(ExternalNavigationService);

  /** Élément à montrer. `null` ferme la modale. */
  @Input() set item(value: PreviewItem | null) {
    this.open(value, null);
  }

  /** Chargeur d'un élément sans URL : un flux, d'où qu'il vienne. */
  @Input() load?: () => Observable<Blob | null>;

  /**
   * Support de bibliothèque — l'entrée historique, qui se lit avec
   * `download` : le chemin de téléchargement diffère entre administration,
   * professeur et élève, c'est l'écran hôte qui le connaît.
   */
  @Input() set material(value: CourseMaterial | null) {
    this.open(value ? {
      title: value.title,
      category: value.category,
      meta: [value.subjectName, value.professorFullName].filter((x): x is string => !!x),
      externalUrl: value.externalUrl,
      url: streamable(value),
    } : null, value?.id ?? null);
  }

  @Input() download?: (id: number) => Observable<HttpResponse<Blob>>;

  @Output() closed = new EventEmitter<void>();

  readonly current = signal<PreviewItem | null>(null);
  readonly loading = signal(false);
  readonly errored = signal(false);
  readonly objectUrl = signal<SafeResourceUrl | SafeUrl | null>(null);

  private materialId: number | null = null;
  private rawUrl: string | null = null;
  private inflight: Subscription | null = null;
  /** L'élément déjà chargé — un changement d'une autre entrée ne recharge pas. */
  private fetched: PreviewItem | null = null;

  constructor() {
    // Une modale fermée par navigation ne passe pas par close() : on libère
    // aussi à la destruction, sinon le blob fuit.
    inject(DestroyRef).onDestroy(() => this.release());
  }

  ngOnChanges(): void {
    const item = this.current();
    if (item && item !== this.fetched) {
      this.fetched = item;
      this.fetch(item);
    }
  }

  /** Rendu à appliquer, déduit de la catégorie calculée par le serveur. */
  get kind(): PreviewKind {
    const m = this.current();
    if (!m) return 'unsupported';
    if (m.externalUrl) return 'external';

    switch (m.category) {
      case 'Pdf':   return 'pdf';
      case 'Image': return 'image';
      case 'Video': return 'video';
      case 'Audio': return 'audio';
      default:      return 'unsupported';
    }
  }

  close(): void {
    this.release();
    this.current.set(null);
    this.fetched = null;
    this.closed.emit();
  }

  openExternal(): void {
    this.navigation.open(this.current()?.externalUrl);
  }

  /** Le menu contextuel offrirait « Enregistrer sous » : il n'a pas sa place ici. */
  block(event: Event): void {
    event.preventDefault();
  }

  private open(item: PreviewItem | null, materialId: number | null): void {
    this.release();
    this.current.set(item);
    this.materialId = materialId;
  }

  private fetch(item: PreviewItem): void {
    // Un lien externe s'ouvre chez son hébergeur : rien à charger, et
    // l'intégrer dans une iframe se heurterait à leurs en-têtes anti-cadrage.
    if (item.externalUrl) return;

    // Un type qu'on ne sait pas rendre n'a pas à être chargé pour rien.
    if (this.kind === 'unsupported') return;

    // Une URL lisible : le navigateur la lit en flux, rien ne transite ici.
    if (item.url) {
      this.objectUrl.set(this.trust(item.url));
      return;
    }

    const kind = this.kind;
    const source = this.source();
    if (!source) {
      this.errored.set(true);
      return;
    }

    this.loading.set(true);
    this.errored.set(false);

    this.inflight = source.subscribe({
      next: (blob) => {
        if (!blob) {
          this.errored.set(true);
          this.loading.set(false);
          return;
        }

        this.rawUrl = URL.createObjectURL(retype(blob, kind));
        this.objectUrl.set(this.trust(this.rawUrl));
        this.loading.set(false);
      },
      error: () => {
        this.errored.set(true);
        this.loading.set(false);
      },
    });
  }

  /**
   * L'URL vient de notre propre API ou d'un lien qu'elle a signé : contourner
   * l'assainisseur est délibéré, Angular bloquerait sinon une URL `blob:` dans
   * un iframe ou un `<video>`. Pour un PDF, la visionneuse du navigateur perd
   * sa barre d'outils — et avec elle ses boutons d'enregistrement et d'impression.
   */
  private trust(url: string): SafeResourceUrl | SafeUrl {
    return this.kind === 'pdf'
      ? this.sanitizer.bypassSecurityTrustResourceUrl(`${url}#toolbar=0&navpanes=0`)
      : this.sanitizer.bypassSecurityTrustUrl(url);
  }

  /** Le flux à montrer : le chargeur fourni, ou le téléchargement d'un support. */
  private source(): Observable<Blob | null> | null {
    if (this.load) return this.load();
    if (this.download && this.materialId !== null) {
      return this.download(this.materialId).pipe(map((res) => res.body));
    }
    return null;
  }

  private release(): void {
    // Fermer en plein chargement abandonne la requête : rien n'arrive sur une modale close.
    this.inflight?.unsubscribe();
    this.inflight = null;
    if (this.rawUrl) {
      URL.revokeObjectURL(this.rawUrl);
      this.rawUrl = null;
    }
    this.objectUrl.set(null);
    this.loading.set(false);
    this.errored.set(false);
  }
}
